// src/components/Admin/Cantieri/CantieriManagement.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy,
  getDoc
} from 'firebase/firestore';
import { db } from '../../../firebase';

// Import componenti rapportini
import RapportiniManagement from './RapportiniManagement';
import RapportinoDetail from './RapportinoDetail';
import GalleriaCantiere from './GalleriaCantiere';

// Import del nuovo componente Portal
import SettingsDropdownPortal from './SettingsDropdownPortal';

import './CantieriManagement.css';

// AGGIORNATO: Componente accetta props per navigazione specifica
const CantieriManagement = ({ 
  preselectedCommittenteId = null, 
  showCreateForm = false,
  selectedCantiereId = null,
  showCantiereDetail = false,
  viewMode = null,
  onBackToCommittente = null,
  onBackToCantieri = null
}) => {
  const [cantieri, setCantieri] = useState([]);
  const [filteredCantieri, setFilteredCantieri] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [responsabili, setResponsabili] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCantiere, setEditingCantiere] = useState(null);
  const [selectedCantiere, setSelectedCantiere] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // NUOVO: Stati per gestione rapportini
  const [currentView, setCurrentView] = useState('cantieri'); // 'cantieri', 'rapportini', 'rapportino-detail', 'galleria'
  const [selectedRapportino, setSelectedRapportino] = useState(null);

  // NUOVI STATI per ricerca responsabili
  const [searchResponsabile, setSearchResponsabile] = useState('');
  const [showResponsabiliDropdown, setShowResponsabiliDropdown] = useState(false);

  // AGGIORNATO: Stato per gestione dropdown impostazioni con posizione per Portal
  const [openSettingsDropdown, setOpenSettingsDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Filtri - AGGIORNATO: stato predefinito "Attivo"
  const [filters, setFilters] = useState({
    searchTerm: '',
    committenteId: '',
    stato: 'Attivo', // MODIFICATO: default "Attivo" invece di "all"
    responsabile: '',
    impresa: ''
  });

  // Form state - AGGIORNATO per array responsabili
  const [formData, setFormData] = useState({
    committenteId: '',
    nomeCommessa: '',
    contratto: '',
    luogo: '',
    impresa: '',
    variazioneCostiMateriali: 0,
    dataInizioLavori: '',
    dataFineLavori: '',
    direttoreLavori: '',
    responsabiliCantiere: [],
    note: '',
    stato: 'Attivo'
  });

  // ========================================
  // GESTIONE NAVIGAZIONE DA ADMINPANEL
  // ========================================

  useEffect(() => {
    if (viewMode === 'detail' && selectedCantiereId) {
      // Carica il cantiere specifico per la vista dettaglio
      loadSpecificCantiere(selectedCantiereId);
    } else if (viewMode === 'create' && preselectedCommittenteId && showCreateForm) {
      // Preseleziona committente e apri form
      setFormData(prev => ({
        ...prev,
        committenteId: preselectedCommittenteId
      }));
      setShowForm(true);
    } else {
      // Carica normalmente tutti i cantieri
      loadInitialData();
    }
  }, [viewMode, selectedCantiereId, preselectedCommittenteId, showCreateForm]);

  // AGGIORNATO: Gestione click fuori dal dropdown con supporto per Portal
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Chiudi dropdown impostazioni se si clicca fuori
      if (openSettingsDropdown && 
          !event.target.closest('.settings-dropdown-container') && 
          !event.target.closest('.settings-dropdown-portal')) {
        console.log('🔴 CLICK/TOUCH FUORI DAL DROPDOWN - CHIUDO');
        setOpenSettingsDropdown(null);
      }
      
      // Chiudi dropdown responsabili se si clicca fuori
      if (!event.target.closest('.responsabile-search-container')) {
        setShowResponsabiliDropdown(false);
      }
    };

    // Gestione specifica per touch su mobile
    const handleTouchStart = (event) => {
      handleClickOutside(event);
    };

    // Gestione tasto ESC
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenSettingsDropdown(null);
        setShowResponsabiliDropdown(false);
      }
    };

    // Aggiungi event listeners per diverse interazioni
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSettingsDropdown]);

  const loadSpecificCantiere = async (cantiereId) => {
    try {
      setIsLoading(true);
      console.log('Caricamento cantiere specifico:', cantiereId);
      
      // Carica il cantiere specifico
      const cantiereRef = doc(db, 'cantieri', cantiereId);
      const cantiereSnap = await getDoc(cantiereRef);
      
      if (cantiereSnap.exists()) {
        const data = cantiereSnap.data();
        
        // Gestione sicura delle date - controlla se sono Timestamp o stringhe
        const processDate = (dateField) => {
          if (!dateField) return null;
          
          // Se è un Timestamp di Firestore
          if (dateField.toDate && typeof dateField.toDate === 'function') {
            return dateField.toDate();
          }
          
          // Se è già una stringa o Date
          if (typeof dateField === 'string' || dateField instanceof Date) {
            return new Date(dateField);
          }
          
          // Se è un oggetto con seconds (formato Timestamp serializzato)
          if (dateField.seconds) {
            return new Date(dateField.seconds * 1000);
          }
          
          return null;
        };
        
        const cantiereData = {
          id: cantiereSnap.id,
          ...data,
          dataInizioLavori: processDate(data.dataInizioLavori),
          dataFineLavori: processDate(data.dataFineLavori),
          createdAt: processDate(data.createdAt) || new Date(),
          updatedAt: processDate(data.updatedAt) || new Date()
        };
        
        setSelectedCantiere(cantiereData);
        console.log('Cantiere caricato per dettaglio:', cantiereData);
        
        // Carica anche i dati di supporto
        await loadSupportData();
      } else {
        console.error('Cantiere non trovato:', cantiereId);
        showNotification('Cantiere non trovato', 'error');
      }
    } catch (error) {
      console.error('Errore nel caricamento cantiere:', error);
      showNotification('Errore nel caricamento del cantiere', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSupportData = async () => {
    try {
      const [committentiData, responsabiliData] = await Promise.all([
        getCommittenti(),
        getResponsabiliCantiere()
      ]);
      
      setCommittenti(committentiData);
      setResponsabili(responsabiliData);
    } catch (error) {
      console.error('Errore nel caricamento dati di supporto:', error);
    }
  };

  // Funzioni Firebase locali
  const getCommittenti = async () => {
    try {
      const committentiRef = collection(db, 'committenti');
      const q = query(committentiRef, orderBy('ragioneSociale', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
      });
    } catch (error) {
      console.error('Errore nel recupero committenti:', error);
      return [];
    }
  };

  const getResponsabiliCantiere = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'responsabile'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          nome: data.nome || '',
          cognome: data.cognome || '',
          email: data.email || '',
          role: data.role || 'responsabile',
          displayName: data.nome && data.cognome ? 
            `${data.nome} ${data.cognome}` : 
            data.email
        };
      });
    } catch (error) {
      console.error('Errore nel recupero responsabili:', error);
      return [];
    }
  };

  const getCantieri = async () => {
    try {
      const cantieriRef = collection(db, 'cantieri');
      const q = query(cantieriRef, orderBy('nomeCommessa', 'asc'));
      const querySnapshot = await getDocs(q);
      
      // Funzione di utilità per processare le date
      const processDate = (dateField) => {
        if (!dateField) return null;
        
        // Se è un Timestamp di Firestore
        if (dateField.toDate && typeof dateField.toDate === 'function') {
          return dateField.toDate();
        }
        
        // Se è già una stringa o Date
        if (typeof dateField === 'string' || dateField instanceof Date) {
          return new Date(dateField);
        }
        
        // Se è un oggetto con seconds (formato Timestamp serializzato)
        if (dateField.seconds) {
          return new Date(dateField.seconds * 1000);
        }
        
        return null;
      };
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dataInizioLavori: processDate(data.dataInizioLavori),
          dataFineLavori: processDate(data.dataFineLavori),
          createdAt: processDate(data.createdAt) || new Date(),
          updatedAt: processDate(data.updatedAt) || new Date()
        };
      });
    } catch (error) {
      console.error('Errore nel recupero cantieri:', error);
      return [];
    }
  };

  const createCantiere = async (cantiereData) => {
    try {
      if (!cantiereData.responsabiliCantiere || cantiereData.responsabiliCantiere.length === 0) {
        throw new Error("Almeno un responsabile cantiere è obbligatorio");
      }

      const cantiereToSave = {
        ...cantiereData,
        responsabiliCantiere: cantiereData.responsabiliCantiere,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (cantiereData.dataInizioLavori) {
        cantiereToSave.dataInizioLavori = new Date(cantiereData.dataInizioLavori);
      }

      if (cantiereData.dataFineLavori) {
        cantiereToSave.dataFineLavori = new Date(cantiereData.dataFineLavori);  
      }

      const cantieriRef = collection(db, "cantieri");
      const docRef = await addDoc(cantieriRef, cantiereToSave);
      return docRef.id;
    } catch (error) {
      console.error("Errore nella creazione cantiere:", error);
      throw error;
    }
  };

  const updateCantiere = async (cantiereId, cantiereData) => {
    try {
      if (!cantiereData.responsabiliCantiere || cantiereData.responsabiliCantiere.length === 0) {
        throw new Error("Almeno un responsabile cantiere è obbligatorio");
      }

      const updateData = {
        ...cantiereData,
        responsabiliCantiere: cantiereData.responsabiliCantiere,
        updatedAt: new Date()
      };

      if (cantiereData.dataInizioLavori) {
        updateData.dataInizioLavori = new Date(cantiereData.dataInizioLavori);
      }

      if (cantiereData.dataFineLavori) {
        updateData.dataFineLavori = new Date(cantiereData.dataFineLavori);
      }

      const cantiereRef = doc(db, "cantieri", cantiereId);
      await updateDoc(cantiereRef, updateData);
      return true;
    } catch (error) {
      console.error("Errore nell'aggiornamento cantiere:", error);
      throw error;
    }
  };

  const archiveCantiere = async (cantiereId, isArchiving) => {
    try {
      const cantiereRef = doc(db, "cantieri", cantiereId);
      await updateDoc(cantiereRef, {
        stato: isArchiving ? 'Archiviato' : 'Attivo',
        updatedAt: new Date()
      });
      return true;
    } catch (error) {
      console.error("Errore nell'archiviazione cantiere:", error);
      throw error;
    }
  };

  const deleteCantiere = async (cantiereId) => {
    try {
      const cantiereRef = doc(db, "cantieri", cantiereId);
      await deleteDoc(cantiereRef);
      return true;
    } catch (error) {
      console.error("Errore nell'eliminazione cantiere:", error);
      throw error;
    }
  };

  // Caricamento dati iniziale
  useEffect(() => {
    if (viewMode !== 'detail') {
      loadInitialData();
    }
  }, [viewMode]);

  // GESTIONE props in ingresso per committente preselezionato (solo se non in modalità detail)
  useEffect(() => {
    if (viewMode !== 'detail' && preselectedCommittenteId && showCreateForm) {
      setFormData(prev => ({
        ...prev,
        committenteId: preselectedCommittenteId
      }));
      setShowForm(true);
    }
  }, [preselectedCommittenteId, showCreateForm, viewMode]);

  // Applicazione filtri
  useEffect(() => {
    if (viewMode !== 'detail') {
      applyFilters();
    }
  }, [cantieri, filters, viewMode]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      const [cantieriData, committentiData, responsabiliData] = await Promise.all([
        getCantieri(),
        getCommittenti(),
        getResponsabiliCantiere()
      ]);
      
      setCantieri(cantieriData);
      setCommittenti(committentiData);
      setResponsabili(responsabiliData);
      
      console.log(`Caricati: ${cantieriData.length} cantieri, ${committentiData.length} committenti, ${responsabiliData.length} responsabili`);
    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
      showNotification('Errore nel caricamento dei dati', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...cantieri];

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(cantiere =>
        cantiere.nomeCommessa.toLowerCase().includes(searchLower) ||
        (cantiere.luogo && cantiere.luogo.toLowerCase().includes(searchLower)) ||
        (cantiere.contratto && cantiere.contratto.toLowerCase().includes(searchLower))
      );
    }

    if (filters.committenteId) {
      filtered = filtered.filter(cantiere => cantiere.committenteId === filters.committenteId);
    }

    if (filters.stato !== 'all') {
      filtered = filtered.filter(cantiere => cantiere.stato === filters.stato);
    }

    if (filters.responsabile) {
      filtered = filtered.filter(cantiere => {
        const responsabiliCantiere = cantiere.responsabiliCantiere || [];
        return responsabiliCantiere.includes(filters.responsabile) || 
               cantiere.responsabileCantiere === filters.responsabile;
      });
    }

    if (filters.impresa) {
      filtered = filtered.filter(cantiere => cantiere.impresa === filters.impresa);
    }

    setFilteredCantieri(filtered);
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const getResponsabiliNonAdmin = () => {
    return responsabili.filter(responsabile => responsabile.role !== 'admin');
  };

  const getFilteredResponsabili = () => {
    if (!searchResponsabile.trim()) return [];
    
    const search = searchResponsabile.toLowerCase();
    return getResponsabiliNonAdmin().filter(responsabile => 
      responsabile.displayName.toLowerCase().includes(search) ||
      responsabile.nome.toLowerCase().includes(search) ||
      responsabile.cognome.toLowerCase().includes(search) ||
      responsabile.email.toLowerCase().includes(search)
    );
  };

  const addResponsabile = (responsabile) => {
    if (!formData.responsabiliCantiere.includes(responsabile.id)) {
      setFormData(prev => ({
        ...prev,
        responsabiliCantiere: [...prev.responsabiliCantiere, responsabile.id]
      }));
    }
    setSearchResponsabile('');
    setShowResponsabiliDropdown(false);
  };

  const removeResponsabile = (responsabileId) => {
    setFormData(prev => ({
      ...prev,
      responsabiliCantiere: prev.responsabiliCantiere.filter(id => id !== responsabileId)
    }));
  };

  const getSelectedResponsabili = () => {
    return formData.responsabiliCantiere.map(id => 
      responsabili.find(r => r.id === id)
    ).filter(Boolean);
  };

  const getResponsabiliNames = (responsabiliIds) => {
    if (!responsabiliIds || responsabiliIds.length === 0) return 'Nessun responsabile';
    return responsabiliIds
      .map(id => {
        const responsabile = responsabili.find(r => r.id === id);
        return responsabile ? responsabile.displayName : 'Sconosciuto';
      })
      .join(', ');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nomeCommessa.trim()) {
      showNotification('Il nome cantiere/commessa è obbligatorio', 'error');
      return;
    }
    
    if (!formData.committenteId) {
      showNotification('Seleziona un committente', 'error');
      return;
    }

    if (!formData.responsabiliCantiere || formData.responsabiliCantiere.length === 0) {
      showNotification('Seleziona almeno un responsabile cantiere', 'error');
      return;
    }

    try {
      if (editingCantiere) {
        await updateCantiere(editingCantiere.id, formData);
        showNotification('Cantiere aggiornato con successo', 'success');
      } else {
        await createCantiere(formData);
        showNotification('Cantiere creato con successo', 'success');
      }
      
      resetForm();
      if (viewMode !== 'detail') {
        loadInitialData();
      }
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      showNotification(error.message || 'Errore nel salvataggio del cantiere', 'error');
    }
  };

  const handleEdit = (cantiere) => {
    console.log('🔴 EDIT ACTION:', cantiere.nomeCommessa);
    setEditingCantiere(cantiere);
    setFormData({
      committenteId: cantiere.committenteId || '',
      nomeCommessa: cantiere.nomeCommessa || '',
      contratto: cantiere.contratto || '',
      luogo: cantiere.luogo || '',
      impresa: cantiere.impresa || '',
      variazioneCostiMateriali: cantiere.variazioneCostiMateriali || 0,
      dataInizioLavori: cantiere.dataInizioLavori ? 
        new Date(cantiere.dataInizioLavori).toISOString().split('T')[0] : '',
      dataFineLavori: cantiere.dataFineLavori ? 
        new Date(cantiere.dataFineLavori).toISOString().split('T')[0] : '',
      direttoreLavori: cantiere.direttoreLavori || '',
      responsabiliCantiere: cantiere.responsabiliCantiere 
        ? (Array.isArray(cantiere.responsabiliCantiere) 
            ? cantiere.responsabiliCantiere 
            : [cantiere.responsabiliCantiere])
        : (cantiere.responsabileCantiere ? [cantiere.responsabileCantiere] : []),
      note: cantiere.note || '',
      stato: cantiere.stato || 'Attivo'
    });
    setShowForm(true);
    closeSettingsDropdown();
  };

  const handleArchive = async (cantiere) => {
    console.log('🔴 ARCHIVE ACTION:', cantiere.nomeCommessa);
    const isArchiving = cantiere.stato === 'Attivo';
    const action = isArchiving ? 'archiviare' : 'ripristinare';
    
    if (window.confirm(`Sei sicuro di voler ${action} il cantiere "${cantiere.nomeCommessa}"?`)) {
      try {
        await archiveCantiere(cantiere.id, isArchiving);
        showNotification(`Cantiere ${isArchiving ? 'archiviato' : 'ripristinato'} con successo`, 'success');
        if (viewMode !== 'detail') {
          loadInitialData();
        }
      } catch (error) {
        console.error(`Errore nell'${action}:`, error);
        showNotification(`Errore nell'${action} del cantiere`, 'error');
      }
    }
    closeSettingsDropdown();
  };

  const handleDelete = async (cantiere) => {
    console.log('🔴 DELETE ACTION:', cantiere.nomeCommessa);
    if (window.confirm(`Sei sicuro di voler eliminare definitivamente il cantiere "${cantiere.nomeCommessa}"?\nQuesta azione non può essere annullata.`)) {
      try {
        await deleteCantiere(cantiere.id);
        showNotification('Cantiere eliminato con successo', 'success');
        if (viewMode !== 'detail') {
          loadInitialData();
        }
      } catch (error) {
        console.error('Errore nell\'eliminazione:', error);
        showNotification(error.message || 'Errore nell\'eliminazione del cantiere', 'error');
      }
    }
    closeSettingsDropdown();
  };

  // ========================================
  // NUOVE FUNZIONI PER GESTIRE RAPPORTINI
  // ========================================
  
  const handleGestisciRapportini = (cantiere) => {
    console.log('🔴 RAPPORTINI ACTION:', cantiere.nomeCommessa);
    setSelectedCantiere(cantiere);
    setCurrentView('rapportini');
    closeSettingsDropdown();
  };

  const handleBackFromRapportini = () => {
    setCurrentView('cantieri');
    setSelectedCantiere(null);
    setSelectedRapportino(null);
  };

  const handleViewRapportinoDetail = (rapportino) => {
    setSelectedRapportino(rapportino);
    setCurrentView('rapportino-detail');
  };

  const handleBackFromRapportinoDetail = () => {
    setCurrentView('rapportini');
    setSelectedRapportino(null);
  };

  const handleViewGalleria = (cantiere) => {
    console.log('🔴 GALLERIA ACTION:', cantiere.nomeCommessa);
    setSelectedCantiere(cantiere);
    setCurrentView('galleria');
    closeSettingsDropdown();
  };

  const handleBackFromGalleria = () => {
    setCurrentView('cantieri');
    setSelectedCantiere(null);
  };

  const resetForm = () => {
    setFormData({
      committenteId: preselectedCommittenteId || '', // MODIFICATO: mantiene committente preselezionato
      nomeCommessa: '',
      contratto: '',
      luogo: '',
      impresa: '',
      variazioneCostiMateriali: 0,
      dataInizioLavori: '',
      dataFineLavori: '',
      direttoreLavori: '',
      responsabiliCantiere: [],
      note: '',
      stato: 'Attivo'
    });
    setSearchResponsabile('');
    setShowResponsabiliDropdown(false);
    setEditingCantiere(null);
    setShowForm(false);
  };

  const handleViewDetails = (cantiere) => {
    setSelectedCantiere(cantiere);
  };

  const handleBackToCommittente = (committenteId) => {
    if (onBackToCommittente) {
      console.log('Navigando al dettaglio committente:', committenteId);
      onBackToCommittente(committenteId);
    }
  };

  const handleBackToCantieri = () => {
    if (onBackToCantieri) {
      console.log('Navigando alla lista cantieri');
      onBackToCantieri();
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Non specificata';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const getCommittenteName = (committenteId) => {
    const committente = committenti.find(c => c.id === committenteId);
    return committente ? committente.ragioneSociale : 'Committente non trovato';
  };

  // AGGIORNATE: Funzioni per dropdown impostazioni con calcolo posizione per Portal
  const toggleSettingsDropdown = (cantiereId) => {
    const newOpenState = openSettingsDropdown === cantiereId ? null : cantiereId;
    setOpenSettingsDropdown(newOpenState);
    console.log('🔴 TOGGLE DROPDOWN:', newOpenState ? 'APERTO' : 'CHIUSO', 'per cantiere:', cantiereId);
    
    if (newOpenState) {
      // Calcola la posizione del dropdown
      setTimeout(() => {
        const button = document.querySelector(`[data-cantiere-id="${cantiereId}"] .settings-dropdown-container button`);
        
        if (button) {
          const rect = button.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const dropdownHeight = 300; // Altezza stimata del dropdown
          const dropdownWidth = 220; // Larghezza stimata del dropdown
          
          // Calcola posizione ottimale
          let top = rect.bottom + 5; // 5px sotto il pulsante
          let left = rect.left;
          
          // Su mobile, centra il dropdown
          if (window.innerWidth <= 768) {
            top = viewportHeight / 2 - dropdownHeight / 2;
            left = viewportWidth / 2 - dropdownWidth / 2;
          } else {
            // Desktop: Se il dropdown esce fuori dalla viewport in basso, aprilo sopra
            if (top + dropdownHeight > viewportHeight) {
              top = rect.top - dropdownHeight - 5;
            }
            
            // Se esce fuori a destra, spostalo a sinistra
            if (left + dropdownWidth > viewportWidth) {
              left = viewportWidth - dropdownWidth - 10; // 10px di margine
            }
            
            // Se esce fuori a sinistra
            if (left < 10) {
              left = 10;
            }
          }
          
          setDropdownPosition({ top, left });
          console.log('🔴 POSIZIONAMENTO DROPDOWN:', { top, left, buttonRect: rect });
        }
      }, 10);
    }
  };

  const closeSettingsDropdown = () => {
    console.log('🔴 CHIUSURA DROPDOWN');
    setOpenSettingsDropdown(null);
  };

  // Funzione per determinare se siamo su mobile
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Caricamento cantieri...</p>
      </div>
    );
  }

  // ========================================
  // RENDERING VISTE RAPPORTINI
  // ========================================
  
  // Vista gestione rapportini
  if (currentView === 'rapportini' && selectedCantiere) {
    return (
      <RapportiniManagement 
        cantiere={selectedCantiere}
        onBack={handleBackFromRapportini}
        onViewDetail={handleViewRapportinoDetail}
        onViewGalleria={() => setCurrentView('galleria')}
      />
    );
  }

  // Vista dettaglio rapportino
  if (currentView === 'rapportino-detail' && selectedRapportino && selectedCantiere) {
    return (
      <RapportinoDetail 
        rapportino={selectedRapportino}
        cantiere={selectedCantiere}
        onBack={handleBackFromRapportinoDetail}
        onEdit={(rapportino) => {
          // Implementare logica di modifica
          console.log('Modifica rapportino:', rapportino);
        }}
        onDuplicate={(rapportino) => {
          // Implementare logica di duplicazione
          console.log('Duplica rapportino:', rapportino);
        }}
        onDelete={(rapportino) => {
          // Implementare logica di eliminazione
          console.log('Elimina rapportino:', rapportino);
        }}
      />
    );
  }

  // Vista galleria
  if (currentView === 'galleria' && selectedCantiere) {
    return (
      <GalleriaCantiere 
        cantiere={selectedCantiere}
        onBack={handleBackFromGalleria}
      />
    );
  }

  return (
    <div className="cantieri-management">
      {/* Notifiche - SEMPRE visibili */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* VISTA DETTAGLIO CANTIERE SPECIFICO (da click committenti) */}
      {viewMode === 'detail' && selectedCantiere && (
        <CantiereDetailView
          cantiere={selectedCantiere}
          committenteName={getCommittenteName(selectedCantiere.committenteId)}
          responsabiliNames={getResponsabiliNames(
            selectedCantiere.responsabiliCantiere || 
            (selectedCantiere.responsabileCantiere ? [selectedCantiere.responsabileCantiere] : [])
          )}
          onBackToCommittente={() => handleBackToCommittente(selectedCantiere.committenteId)}
          onBackToCantieri={handleBackToCantieri}
          onEdit={() => {
            console.log('Apertura modifica per cantiere:', selectedCantiere.id);
            handleEdit(selectedCantiere);
          }}
          onGestisciRapportini={() => handleGestisciRapportini(selectedCantiere)}
          onViewGalleria={() => handleViewGalleria(selectedCantiere)}
        />
      )}

      {/* VISTA DETTAGLIO CANTIERE STANDARD (da tabella cantieri) */}
      {selectedCantiere && viewMode !== 'detail' && (
        <CantiereDetail 
          cantiere={selectedCantiere}
          committenteName={getCommittenteName(selectedCantiere.committenteId)}
          responsabiliNames={getResponsabiliNames(
            selectedCantiere.responsabiliCantiere || 
            (selectedCantiere.responsabileCantiere ? [selectedCantiere.responsabileCantiere] : [])
          )}
          onBack={() => setSelectedCantiere(null)}
          onEdit={() => {
            handleEdit(selectedCantiere);
            setSelectedCantiere(null);
          }}
          onGestisciRapportini={() => handleGestisciRapportini(selectedCantiere)}
          onViewGalleria={() => handleViewGalleria(selectedCantiere)}
        />
      )}

      {/* VISTA PRINCIPALE - solo se non siamo in vista dettaglio */}
      {!selectedCantiere && viewMode !== 'detail' && (
        <>
          {/* Header */}
          <div className="section-header">
            <h2>🏗️ Gestione Cantieri</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              ➕ Nuovo Cantiere
            </button>
          </div>

          {/* Filtri */}
          <div className="filters-container">
            <div className="filters-grid">
              <div className="filter-group">
                <input
                  type="text"
                  placeholder="Cerca per nome cantiere, luogo o contratto..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                  className="filter-input"
                />
              </div>

              <div className="filter-group">
                <select
                  value={filters.committenteId}
                  onChange={(e) => setFilters({...filters, committenteId: e.target.value})}
                  className="filter-select"
                >
                  <option value="">Tutti i committenti</option>
                  {committenti.map(committente => (
                    <option key={committente.id} value={committente.id}>
                      {committente.ragioneSociale}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <select
                  value={filters.stato}
                  onChange={(e) => setFilters({...filters, stato: e.target.value})}
                  className="filter-select"
                >
                  <option value="all">Tutti gli stati</option>
                  <option value="Attivo">Attivi</option>
                  <option value="Archiviato">Archiviati</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tabella Cantieri - AGGIORNATA CON PORTAL */}
          {filteredCantieri.length > 0 ? (
            <>
              {/* TABELLA DESKTOP - AGGIORNATA CON PORTAL */}
              <div className="table-container hide-mobile">
                <table className="cantieri-table">
                  <thead>
                    <tr>
                      <th>Nome Cantiere</th>
                      <th>Luogo</th>
                      <th>Data Inizio</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCantieri.map(cantiere => (
                      <tr key={cantiere.id} data-cantiere-id={cantiere.id}>
                        <td>
                          <div className="cantiere-info">
                            <strong>{cantiere.nomeCommessa}</strong>
                            {cantiere.contratto && (
                              <small>Contratto: {cantiere.contratto}</small>
                            )}
                          </div>
                        </td>
                        <td>{cantiere.luogo || 'Non specificato'}</td>
                        <td>{formatDate(cantiere.dataInizioLavori)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-info btn-sm"
                              onClick={() => handleViewDetails(cantiere)}
                              title="Visualizza dettagli"
                            >
                              👁️ Dettagli
                            </button>
                            <div className="settings-dropdown-container">
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => toggleSettingsDropdown(cantiere.id)}
                                title="Impostazioni"
                              >
                                ⚙️ Impostazioni
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* VISTA MOBILE - AGGIORNATA CON PORTAL */}
              <div className="cantieri-mobile-view show-mobile">
                {filteredCantieri.map(cantiere => (
                  <div key={cantiere.id} className="cantiere-mobile-card" data-cantiere-id={cantiere.id}>
                    <div className="cantiere-mobile-card-header">
                      <h3 className="cantiere-mobile-card-title">
                        {cantiere.nomeCommessa}
                      </h3>
                    </div>
                    
                    <div className="cantiere-mobile-card-content">
                      <div className="cantiere-mobile-card-info">
                        <div className="cantiere-mobile-card-info-item">
                          <span className="cantiere-mobile-card-info-label">📍 Luogo:</span>
                          <span className="cantiere-mobile-card-info-value">
                            {cantiere.luogo || 'Non specificato'}
                          </span>
                        </div>
                        
                        <div className="cantiere-mobile-card-info-item">
                          <span className="cantiere-mobile-card-info-label">📅 Data Inizio:</span>
                          <span className="cantiere-mobile-card-info-value">
                            {formatDate(cantiere.dataInizioLavori)}
                          </span>
                        </div>
                        
                        {cantiere.contratto && (
                          <div className="cantiere-mobile-card-info-item">
                            <span className="cantiere-mobile-card-info-label">📄 Contratto:</span>
                            <span className="cantiere-mobile-card-info-value">
                              {cantiere.contratto}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="cantiere-mobile-card-actions">
                      <button 
                        className="btn btn-info"
                        onClick={() => handleViewDetails(cantiere)}
                        title="Visualizza dettagli"
                      >
                        👁️ Dettagli
                      </button>
                      
                      <div className="settings-dropdown-container">
                        <button 
                          className="btn btn-secondary"
                          onClick={() => toggleSettingsDropdown(cantiere.id)}
                          title="Impostazioni"
                        >
                          ⚙️ Impostazioni
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <h3>Nessun cantiere trovato</h3>
              <p>
                {cantieri.length === 0 ? 
                  'Inizia creando il primo cantiere' : 
                  'Prova a modificare i filtri di ricerca'
                }
              </p>
            </div>
          )}
        </>
      )}

      {/* PORTAL DROPDOWN - RENDERIZZATO SOLO QUANDO NECESSARIO */}
      {openSettingsDropdown && (
        <SettingsDropdownPortal 
          top={dropdownPosition.top}
          left={dropdownPosition.left}
          isMobile={isMobile()}
          onClose={closeSettingsDropdown}
        >
          <button
            className="dropdown-item"
            onClick={() => {
              console.log('🔴 PORTAL CLICK: Gestisci Rapportini');
              const cantiere = filteredCantieri.find(c => c.id === openSettingsDropdown);
              if (cantiere) handleGestisciRapportini(cantiere);
            }}
          >
            📝 Gestisci Rapportini
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              console.log('🔴 PORTAL CLICK: Galleria');
              const cantiere = filteredCantieri.find(c => c.id === openSettingsDropdown);
              if (cantiere) handleViewGalleria(cantiere);
            }}
          >
            🖼️ Galleria
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              console.log('🔴 PORTAL CLICK: Modifica');
              const cantiere = filteredCantieri.find(c => c.id === openSettingsDropdown);
              if (cantiere) handleEdit(cantiere);
            }}
          >
            ✏️ Modifica
          </button>
          <button
            className={`dropdown-item ${filteredCantieri.find(c => c.id === openSettingsDropdown)?.stato === 'Attivo' ? 'warning' : 'success'}`}
            onClick={() => {
              console.log('🔴 PORTAL CLICK: Archive');
              const cantiere = filteredCantieri.find(c => c.id === openSettingsDropdown);
              if (cantiere) handleArchive(cantiere);
            }}
          >
            {filteredCantieri.find(c => c.id === openSettingsDropdown)?.stato === 'Attivo' ? '📁 Archivia' : '📂 Ripristina'}
          </button>
          {filteredCantieri.find(c => c.id === openSettingsDropdown)?.stato === 'Archiviato' && (
            <button
              className="dropdown-item danger"
              onClick={() => {
                console.log('🔴 PORTAL CLICK: Delete');
                const cantiere = filteredCantieri.find(c => c.id === openSettingsDropdown);
                if (cantiere) handleDelete(cantiere);
              }}
            >
              🗑️ Elimina
            </button>
          )}
        </SettingsDropdownPortal>
      )}

      {/* MODAL FORM - SEMPRE RENDERIZZATO */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>
                {editingCantiere ? 'Modifica Cantiere' : 'Nuovo Cantiere'}
                {preselectedCommittenteId && !editingCantiere && (
                  <small style={{ display: 'block', fontWeight: 'normal', fontSize: '0.9rem', color: '#7f8c8d', marginTop: '5px' }}>
                    per {getCommittenteName(preselectedCommittenteId)}
                  </small>
                )}
              </h3>
              <button className="modal-close" onClick={resetForm}>✖️</button>
            </div>
            
            <form onSubmit={handleFormSubmit} className="cantiere-form">
              <div className="form-grid">
                {/* Committente */}
                <div className="form-group">
                  <label>
                    Committente *
                    {preselectedCommittenteId && !editingCantiere && (
                      <small style={{ marginLeft: '10px', color: '#27ae60', fontWeight: 'normal' }}>
                        (Preselezionato)
                      </small>
                    )}
                  </label>
                  <select
                    value={formData.committenteId}
                    onChange={(e) => setFormData({...formData, committenteId: e.target.value})}
                    required
                    disabled={preselectedCommittenteId && !editingCantiere}
                    style={preselectedCommittenteId && !editingCantiere ? 
                      { backgroundColor: '#e8f5e8', border: '2px solid #27ae60' } : 
                      {}
                    }
                  >
                    <option value="">Seleziona committente...</option>
                    {committenti.map(committente => (
                      <option key={committente.id} value={committente.id}>
                        {committente.ragioneSociale}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Nome Cantiere / Commessa *</label>
                  <input
                    type="text"
                    value={formData.nomeCommessa}
                    onChange={(e) => setFormData({...formData, nomeCommessa: e.target.value})}
                    required
                    placeholder="Es: Cantiere Via Roma"
                  />
                </div>

                <div className="form-group">
                  <label>Contratto</label>
                  <input
                    type="text"
                    value={formData.contratto}
                    onChange={(e) => setFormData({...formData, contratto: e.target.value})}
                    placeholder="Numero o riferimento contratto"
                  />
                </div>

                <div className="form-group">
                  <label>Luogo</label>
                  <input
                    type="text"
                    value={formData.luogo}
                    onChange={(e) => setFormData({...formData, luogo: e.target.value})}
                    placeholder="Indirizzo del cantiere"
                  />
                </div>

                <div className="form-group">
                  <label>Impresa</label>
                  <input
                    type="text"
                    value={formData.impresa}
                    onChange={(e) => setFormData({...formData, impresa: e.target.value})}
                    placeholder="Nome impresa"
                  />
                </div>

                <div className="form-group">
                  <label>Costo Trasporto (€/ton)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.variazioneCostiMateriali}
                    onChange={(e) => setFormData({...formData, variazioneCostiMateriali: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                  />
                  <small>Sovrapprezzo trasporto aggiunto ai materiali a costo fisso</small>
                </div>

                <div className="form-group">
                  <label>Data Inizio Lavori</label>
                  <input
                    type="date"
                    value={formData.dataInizioLavori}
                    onChange={(e) => setFormData({...formData, dataInizioLavori: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Data Fine Lavori</label>
                  <input
                    type="date"
                    value={formData.dataFineLavori}
                    onChange={(e) => setFormData({...formData, dataFineLavori: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Direttore dei Lavori</label>
                  <input
                    type="text"
                    value={formData.direttoreLavori}
                    onChange={(e) => setFormData({...formData, direttoreLavori: e.target.value})}
                    placeholder="Nome e contatto del direttore lavori"
                  />
                </div>

                <div className="form-group">
                  <label>Stato</label>
                  <select
                    value={formData.stato}
                    onChange={(e) => setFormData({...formData, stato: e.target.value})}
                  >
                    <option value="Attivo">Attivo</option>
                    <option value="Archiviato">Archiviato</option>
                  </select>
                </div>
              </div>

              {/* SEZIONE RESPONSABILI */}
              <div className="form-group full-width">
                <label>Responsabili Cantiere * ({formData.responsabiliCantiere.length} selezionati)</label>
                
                <div className="responsabile-search-container">
                  <input
                    type="text"
                    placeholder="Cerca responsabile per nome..."
                    value={searchResponsabile}
                    onChange={(e) => {
                      setSearchResponsabile(e.target.value);
                      setShowResponsabiliDropdown(e.target.value.trim().length > 0);
                    }}
                    onFocus={() => setShowResponsabiliDropdown(searchResponsabile.trim().length > 0)}
                    className="responsabile-search-input"
                  />
                  
                  {showResponsabiliDropdown && getFilteredResponsabili().length > 0 && (
                    <div className="responsabili-dropdown">
                      {getFilteredResponsabili().map(responsabile => (
                        <div
                          key={responsabile.id}
                          className={`responsabile-dropdown-item ${
                            formData.responsabiliCantiere.includes(responsabile.id) ? 'already-selected' : ''
                          }`}
                          onClick={() => addResponsabile(responsabile)}
                        >
                          <div className="responsabile-dropdown-info">
                            <div className="responsabile-dropdown-name">{responsabile.displayName}</div>
                            <div className="responsabile-dropdown-role">{responsabile.role}</div>
                          </div>
                          {formData.responsabiliCantiere.includes(responsabile.id) && (
                            <div className="responsabile-dropdown-selected">✅</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {showResponsabiliDropdown && searchResponsabile.trim().length > 0 && getFilteredResponsabili().length === 0 && (
                    <div className="responsabili-dropdown">
                      <div className="responsabile-dropdown-item no-results">
                        Nessun responsabile trovato per "{searchResponsabile}"
                      </div>
                    </div>
                  )}
                </div>
                
                {formData.responsabiliCantiere.length > 0 && (
                  <div className="selected-responsabili">
                    <small>Responsabili selezionati:</small>
                    <div className="selected-responsabili-list">
                      {getSelectedResponsabili().map(responsabile => (
                        <div key={responsabile.id} className="selected-responsabile-tag">
                          <span className="selected-responsabile-name">{responsabile.displayName}</span>
                          <button
                            type="button"
                            className="remove-responsabile-btn"
                            onClick={() => removeResponsabile(responsabile.id)}
                            title="Rimuovi responsabile"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {getResponsabiliNonAdmin().length === 0 && (
                  <div className="no-responsabili-message">
                    <p>⚠️ Nessun responsabile disponibile</p>
                    <small>Gli admin possono lavorare su tutti i cantieri automaticamente</small>
                  </div>
                )}
              </div>

              <div className="form-group full-width">
                <label>Note</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Note aggiuntive sul cantiere..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={formData.responsabiliCantiere.length === 0}
                >
                  {editingCantiere ? 'Aggiorna' : 'Conferma Creazione'} Cantiere
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// COMPONENTE VISTA DETTAGLIO CANTIERE SPECIFICO (da committenti)
const CantiereDetailView = ({ 
  cantiere, 
  committenteName, 
  responsabiliNames, 
  onBackToCommittente, 
  onBackToCantieri, 
  onEdit,
  onGestisciRapportini,
  onViewGalleria
}) => {
    const formatDate = (date) => {
      if (!date) return 'Non specificata';
      return new Date(date).toLocaleDateString('it-IT');
    };
  
    return (
      <div className="cantiere-detail-view">
        {/* Header specifico per vista da committenti con doppia navigazione */}
        <div className="detail-header special">
          <div className="navigation-buttons">
            <button className="btn-back primary" onClick={onBackToCommittente}>
              ← Torna al Committente
            </button>
            <button className="btn-back secondary" onClick={onBackToCantieri}>
              🏗️ Torna ai Cantieri
            </button>
          </div>
          <div className="header-content">
            <h2>🏗️ {cantiere.nomeCommessa}</h2>
            <div className="header-subtitle">
              <span className="committente-name">📋 {committenteName}</span>
              <span className={`status-badge large ${cantiere.stato === 'Attivo' ? 'active' : 'archived'}`}>
                {cantiere.stato}
              </span>
            </div>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-secondary"
              onClick={onEdit}
            >
              ✏️ Modifica Cantiere
            </button>
            <button 
              className="btn btn-success"
              onClick={onViewGalleria}
            >
              🖼️ Galleria
            </button>
            <button 
              className="btn btn-primary"
              onClick={onGestisciRapportini}
            >
              📝 Gestisci Rapportini
            </button>
          </div>
        </div>
  
        {/* Informazioni dettagliate del cantiere */}
        <div className="cantiere-info-detailed">
          <div className="info-grid-detailed">
            <div className="info-card-large">
              <h3>📋 Informazioni Generali</h3>
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-label">🏢 Committente:</span>
                  <span className="info-value">{committenteName}</span>
                </div>
                
                {cantiere.contratto && (
                  <div className="info-row">
                    <span className="info-label">📄 Contratto:</span>
                    <span className="info-value">{cantiere.contratto}</span>
                  </div>
                )}
                
                {cantiere.luogo && (
                  <div className="info-row">
                    <span className="info-label">📍 Luogo:</span>
                    <span className="info-value">{cantiere.luogo}</span>
                  </div>
                )}
                
                {cantiere.impresa && (
                  <div className="info-row">
                    <span className="info-label">🏗️ Impresa:</span>
                    <span className="info-value">{cantiere.impresa}</span>
                  </div>
                )}
                
                <div className="info-row">
                  <span className="info-label">👷 Responsabili:</span>
                  <span className="info-value">{responsabiliNames}</span>
                </div>
                
                {cantiere.direttoreLavori && (
                  <div className="info-row">
                    <span className="info-label">👨‍💼 Direttore Lavori:</span>
                    <span className="info-value">{cantiere.direttoreLavori}</span>
                  </div>
                )}
              </div>
            </div>
  
            <div className="info-card-large">
              <h3>📅 Date e Tempistiche</h3>
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-label">🚀 Data Inizio:</span>
                  <span className="info-value">{formatDate(cantiere.dataInizioLavori)}</span>
                </div>
                
                {cantiere.dataFineLavori && (
                  <div className="info-row">
                    <span className="info-label">🏁 Data Fine:</span>
                    <span className="info-value">{formatDate(cantiere.dataFineLavori)}</span>
                  </div>
                )}
              </div>
            </div>
  
            {(cantiere.variazioneCostiMateriali > 0 || cantiere.note) && (
              <div className="info-card-large">
                <h3>💰 Costi e Note</h3>
                <div className="info-rows">
                  {cantiere.variazioneCostiMateriali > 0 && (
                    <div className="info-row">
                      <span className="info-label">🚛 Costo Trasporto:</span>
                      <span className="info-value cost">€{cantiere.variazioneCostiMateriali.toFixed(2)}/ton</span>
                    </div>
                  )}
                  
                  {cantiere.note && (
                    <div className="info-row full-width">
                      <span className="info-label">📝 Note:</span>
                      <span className="info-value note">{cantiere.note}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
  
        {/* Sezione rapportini */}
        <div className="rapportini-section">
          <div className="section-header">
            <h3>📝 Rapportini</h3>
            <div className="section-actions">
              <button 
                className="btn btn-secondary"
                onClick={onViewGalleria}
              >
                🖼️ Galleria
              </button>
              <button 
                className="btn btn-primary"
                onClick={onGestisciRapportini}
              >
                📝 Gestisci Rapportini
              </button>
            </div>
          </div>
          
          <div className="preview-content">
            <div className="preview-card">
              <h4>🚀 Funzionalità Disponibili</h4>
              <ul>
                <li>✅ Visualizzazione completa rapportini</li>
                <li>✅ Creazione e modifica rapportini</li>
                <li>✅ Gestione personale, mezzi e materiali</li>
                <li>✅ Upload e gestione allegati</li>
                <li>✅ Galleria immagini del cantiere</li>
                <li>✅ Report economici dettagliati</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

// COMPONENTE DETTAGLIO CANTIERE STANDARD (da tabella cantieri)
const CantiereDetail = ({ 
  cantiere, 
  committenteName, 
  responsabiliNames, 
  onBack, 
  onEdit,
  onGestisciRapportini,
  onViewGalleria
}) => {
  const formatDate = (date) => {
    if (!date) return 'Non specificata';
    return new Date(date).toLocaleDateString('it-IT');
  };

  return (
    <div className="cantiere-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={onBack}>
          ← Torna ai Cantieri
        </button>
        <h2>🏗️ {cantiere.nomeCommessa}</h2>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={onEdit}
          >
            ✏️ Modifica Cantiere
          </button>
          <button 
            className="btn btn-success"
            onClick={onViewGalleria}
          >
            🖼️ Galleria
          </button>
          <button 
            className="btn btn-primary"
            onClick={onGestisciRapportini}
          >
            📝 Gestisci Rapportini
          </button>
        </div>
      </div>

      <div className="cantiere-info">
        <div className="info-card">
          <h3>Informazioni Generali</h3>
          <div className="info-grid">
            <div className="info-item">
              <strong>Committente:</strong> {committenteName}
            </div>
            <div className="info-item">
              <strong>Stato:</strong> 
              <span className={`status-badge ${cantiere.stato.toLowerCase()}`}>
                {cantiere.stato}
              </span>
            </div>
            {cantiere.contratto && (
              <div className="info-item">
                <strong>Contratto:</strong> {cantiere.contratto}
              </div>
            )}
            {cantiere.luogo && (
              <div className="info-item">
                <strong>Luogo:</strong> {cantiere.luogo}
              </div>
            )}
            {cantiere.impresa && (
              <div className="info-item">
                <strong>Impresa:</strong> {cantiere.impresa}
              </div>
            )}
            <div className="info-item">
              <strong>Responsabili:</strong> {responsabiliNames}
            </div>
            {cantiere.direttoreLavori && (
              <div className="info-item">
                <strong>Direttore Lavori:</strong> {cantiere.direttoreLavori}
              </div>
            )}
            <div className="info-item">
              <strong>Data Inizio:</strong> {formatDate(cantiere.dataInizioLavori)}
            </div>
            {cantiere.dataFineLavori && (
              <div className="info-item">
                <strong>Data Fine:</strong> {formatDate(cantiere.dataFineLavori)}
              </div>
            )}
            {cantiere.variazioneCostiMateriali > 0 && (
              <div className="info-item">
                <strong>Costo Trasporto:</strong> €{cantiere.variazioneCostiMateriali.toFixed(2)}/ton
              </div>
            )}
            {cantiere.note && (
              <div className="info-item full-width">
                <strong>Note:</strong> {cantiere.note}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rapportini-section">
        <div className="section-header">
          <h3>📝 Rapportini</h3>
          <div className="section-actions">
            <button 
              className="btn btn-secondary"
              onClick={onViewGalleria}
            >
              🖼️ Galleria
            </button>
            <button 
              className="btn btn-primary"
              onClick={onGestisciRapportini}
            >
              📝 Gestisci Rapportini
            </button>
          </div>
        </div>
        
        <div className="preview-content">
          <div className="preview-card">
            <h4>🎯 Sistema Rapportini Completo</h4>
            <p>Clicca su "Gestisci Rapportini" per accedere a tutte le funzionalità:</p>
            <ul>
              <li>Creazione e modifica rapportini giornalieri</li>
              <li>Gestione completa di personale, mezzi e materiali</li>
              <li>Upload di foto e documenti</li>
              <li>Calcoli automatici di costi e ricavi</li>
              <li>Reportistica avanzata</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CantieriManagement;